import {
  OFFLINE_CACHE_SCHEMA_VERSION,
  OFFLINE_DB_NAME,
  OFFLINE_SNAPSHOT_TYPES,
  OFFLINE_STORE_NAME,
  buildOfflineSnapshotKey,
  normalizeOfflineMemberId,
} from './offlineConstants';

/**
 * Production uses browser IndexedDB.
 * Tests may inject an in-memory backend via __setOfflineSnapshotBackendForTests.
 *
 * @typedef {object} OfflineSnapshotRecord
 * @property {string} key
 * @property {number} schemaVersion
 * @property {string} memberId
 * @property {string} journeyId
 * @property {string} snapshotType
 * @property {string} fetchedAt
 * @property {*} payload
 */

/** @type {null | {
 *   put: (record: OfflineSnapshotRecord) => Promise<void>,
 *   get: (key: string) => Promise<OfflineSnapshotRecord | null>,
 *   deleteKey: (key: string) => Promise<void>,
 *   deleteByMemberId: (memberId: string) => Promise<number>,
 * }} */
let testBackend = null;

let dbPromise = null;

/**
 * @param {typeof testBackend} backend
 */
export function __setOfflineSnapshotBackendForTests(backend) {
  testBackend = backend;
  dbPromise = null;
}

export function __resetOfflineSnapshotStoreForTests() {
  testBackend = null;
  dbPromise = null;
}

export function createMemoryOfflineSnapshotBackend() {
  /** @type {Map<string, OfflineSnapshotRecord>} */
  const rows = new Map();
  return {
    put: async (record) => {
      rows.set(record.key, { ...record, payload: structuredCloneSafe(record.payload) });
    },
    get: async (key) => {
      const hit = rows.get(key);
      return hit ? { ...hit, payload: structuredCloneSafe(hit.payload) } : null;
    },
    deleteKey: async (key) => {
      rows.delete(key);
    },
    deleteByMemberId: async (memberId) => {
      const mid = normalizeOfflineMemberId(memberId);
      const keys = [];
      for (const [key, record] of rows.entries()) {
        if (normalizeOfflineMemberId(record.memberId) === mid) {
          keys.push(key);
        }
      }
      keys.forEach((key) => rows.delete(key));
      return keys.length;
    },
    __dump: () => rows,
  };
}

function structuredCloneSafe(value) {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      /* fall through */
    }
  }
  return JSON.parse(JSON.stringify(value ?? null));
}

function assertSnapshotType(snapshotType) {
  if (snapshotType !== OFFLINE_SNAPSHOT_TYPES.OVERVIEW && snapshotType !== OFFLINE_SNAPSHOT_TYPES.TODAY) {
    throw new Error(`unsupported offline snapshotType: ${snapshotType}`);
  }
}

function openDatabase() {
  if (testBackend) {
    return Promise.reject(new Error('test backend active'));
  }
  const factory = typeof indexedDB !== 'undefined' ? indexedDB : null;
  if (!factory) {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      let request;
      try {
        request = factory.open(OFFLINE_DB_NAME, 1);
      } catch (cause) {
        reject(cause);
        return;
      }
      request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(OFFLINE_STORE_NAME)) {
          const store = db.createObjectStore(OFFLINE_STORE_NAME, { keyPath: 'key' });
          store.createIndex('memberId', 'memberId', { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
    }).catch((cause) => {
      dbPromise = null;
      throw cause;
    });
  }
  return dbPromise;
}

async function idbPut(record) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB put failed'));
    tx.objectStore(OFFLINE_STORE_NAME).put(record);
  });
}

async function idbGet(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readonly');
    tx.onerror = () => reject(tx.error || new Error('IndexedDB get failed'));
    const request = tx.objectStore(OFFLINE_STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error || new Error('IndexedDB get failed'));
  });
}

async function idbDeleteKey(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB delete failed'));
    tx.objectStore(OFFLINE_STORE_NAME).delete(key);
  });
}

async function idbDeleteByMemberId(memberId) {
  const db = await openDatabase();
  const mid = normalizeOfflineMemberId(memberId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE_NAME);
    const index = store.index('memberId');
    const request = index.openCursor(IDBKeyRange.only(mid));
    let removed = 0;
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      cursor.delete();
      removed += 1;
      cursor.continue();
    };
    tx.oncomplete = () => resolve(removed);
    tx.onerror = () => reject(tx.error || new Error('IndexedDB member delete failed'));
  });
}

function isUsableRecord(record, expectedMemberId) {
  if (!record || typeof record !== 'object') return false;
  if (record.schemaVersion !== OFFLINE_CACHE_SCHEMA_VERSION) return false;
  const recordMember = normalizeOfflineMemberId(record.memberId);
  const expected = normalizeOfflineMemberId(expectedMemberId);
  if (!recordMember || !expected || recordMember !== expected) return false;
  if (!record.journeyId || !record.snapshotType || !record.fetchedAt) return false;
  if (Object.prototype.hasOwnProperty.call(record, 'authToken')
    || Object.prototype.hasOwnProperty.call(record, 'token')
    || Object.prototype.hasOwnProperty.call(record, 'Authorization')
    || Object.prototype.hasOwnProperty.call(record, 'authorization')) {
    return false;
  }
  return true;
}

/**
 * Persist a private Journey snapshot. Never stores JWT / Authorization.
 * Failures reject; callers must not treat this as Journey API failure.
 */
export async function putOfflineSnapshot({
  memberId,
  journeyId,
  snapshotType,
  payload,
  fetchedAt = new Date().toISOString(),
}) {
  assertSnapshotType(snapshotType);
  const mid = normalizeOfflineMemberId(memberId);
  const jid = journeyId == null ? '' : String(journeyId).trim();
  if (!mid || !jid) {
    throw new Error('putOfflineSnapshot requires memberId and journeyId');
  }
  const record = {
    key: buildOfflineSnapshotKey(mid, jid, snapshotType),
    schemaVersion: OFFLINE_CACHE_SCHEMA_VERSION,
    memberId: mid,
    journeyId: jid,
    snapshotType,
    fetchedAt: String(fetchedAt),
    payload,
  };
  if (testBackend) {
    await testBackend.put(record);
    return record;
  }
  await idbPut(record);
  return record;
}

/**
 * Read a snapshot for the given member only.
 * Incompatible schema / wrong member → delete/ignore and return null.
 */
export async function getOfflineSnapshot({ memberId, journeyId, snapshotType }) {
  assertSnapshotType(snapshotType);
  const mid = normalizeOfflineMemberId(memberId);
  const jid = journeyId == null ? '' : String(journeyId).trim();
  if (!mid || !jid) return null;

  const key = buildOfflineSnapshotKey(mid, jid, snapshotType);
  let record;
  try {
    record = testBackend ? await testBackend.get(key) : await idbGet(key);
  } catch {
    return null;
  }
  if (!record) return null;

  if (record.schemaVersion !== OFFLINE_CACHE_SCHEMA_VERSION) {
    try {
      if (testBackend) await testBackend.deleteKey(key);
      else await idbDeleteKey(key);
    } catch {
      /* ignore cleanup failure */
    }
    return null;
  }

  if (!isUsableRecord(record, mid)) {
    return null;
  }

  return {
    key: record.key,
    schemaVersion: record.schemaVersion,
    memberId: record.memberId,
    journeyId: record.journeyId,
    snapshotType: record.snapshotType,
    fetchedAt: record.fetchedAt,
    payload: record.payload,
  };
}

export async function deleteOfflineSnapshot({ memberId, journeyId, snapshotType }) {
  assertSnapshotType(snapshotType);
  const mid = normalizeOfflineMemberId(memberId);
  const jid = journeyId == null ? '' : String(journeyId).trim();
  if (!mid || !jid) return;
  const key = buildOfflineSnapshotKey(mid, jid, snapshotType);
  if (testBackend) {
    await testBackend.deleteKey(key);
    return;
  }
  await idbDeleteKey(key);
}

/**
 * Wipe all Travel offline snapshots for one member namespace.
 */
export async function deleteMemberOfflineSnapshots(memberId) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid) return 0;
  if (testBackend) {
    return testBackend.deleteByMemberId(mid);
  }
  return idbDeleteByMemberId(mid);
}
